import { authClient } from "@/api/auth-client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { FormError } from "@/shared/components/form-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ResetPasswordFormInput, resetPasswordFormSchema } from "@template/shared";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { PasswordInput } from "./password-input";

interface ResetPasswordFormProps {
  /** Token z URL (?token=…). Prázdný/chybějící → chybová hláška. */
  token: string;
}

/** Nastavení nového hesla z tokenu — sdílené pro pozvánku i reset hesla. */
export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormInput>({
    resolver: zodResolver(resetPasswordFormSchema),
  });

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    setServerError(null);
    const result = await authClient.resetPassword({ newPassword: data.newPassword, token });
    if (result.error) {
      setServerError(
        result.error.message ??
          "Odkaz je neplatný nebo vypršel. Požádejte o nový odkaz na přihlašovací stránce.",
      );
      setIsLoading(false);
      return;
    }
    toast.success("Heslo nastaveno. Můžete se přihlásit.");
    navigate({ to: "/login" });
  });

  if (!token) {
    return (
      <Card className="w-full max-w-md" data-testid="auth-reset-card">
        <CardHeader className="text-center">
          <CardTitle>Neplatný odkaz</CardTitle>
          <CardDescription>
            Odkaz pro nastavení hesla je neúplný nebo vypršel. Požádejte o nový.
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-center">
          <Link
            to="/zapomenute-heslo"
            className="text-sm font-medium text-foreground hover:underline"
            data-testid="auth-reset-request-new"
          >
            Požádat o nový odkaz
          </Link>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md" data-testid="auth-reset-card">
      <CardHeader className="text-center">
        <CardTitle>Nastavení hesla</CardTitle>
        <CardDescription>Zvolte si nové heslo (alespoň 12 znaků).</CardDescription>
      </CardHeader>
      <CardContent>
        <FormError message={serverError ?? undefined} testId="auth-reset-error" />
        <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-reset-form">
          <div className="space-y-2">
            <Label htmlFor="reset-new-password">Nové heslo</Label>
            <PasswordInput
              id="reset-new-password"
              autoFocus
              aria-describedby={errors.newPassword ? "reset-new-password-error" : undefined}
              data-testid="auth-reset-password-input"
              {...register("newPassword")}
            />
            {errors.newPassword ? (
              <p id="reset-new-password-error" role="alert" className="text-sm text-destructive">
                {errors.newPassword.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-confirm-password">Heslo znovu</Label>
            <PasswordInput
              id="reset-confirm-password"
              aria-describedby={errors.confirmPassword ? "reset-confirm-password-error" : undefined}
              data-testid="auth-reset-confirm-input"
              {...register("confirmPassword")}
            />
            {errors.confirmPassword ? (
              <p
                id="reset-confirm-password-error"
                role="alert"
                className="text-sm text-destructive"
              >
                {errors.confirmPassword.message}
              </p>
            ) : null}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            data-testid="auth-reset-submit-button"
          >
            {isLoading ? "Ukládám…" : "Nastavit heslo"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
